import React from "react";
import { Row, Col, Checkbox, Divider } from "antd";
import "./tablePermissionSection.css";

const TablePermissionsSection = ({
  isProfileCase,
  disabledCheckBox,
  permissions = {},
  handlePermissionChange,
  projectStatus,
}) => {
  // Return null when no project is selected (show nothing)
  if (disabledCheckBox) {
    return null;
  }

  const handleCheckboxChange = (section, permissionKey, checked) => {
    // If unchecking the view permission, uncheck all other permissions
    if (section === 'general' && permissionKey === 'view' && !checked) {
      // Create a list of all sections
      const allSections = ['general', 'delivery_notes', 'edit', 'fabrication', 'qc_release', 'corrosion_protection', 'delivery', 'site_receipt', 'installation'];
      
      // For each section, uncheck all permissions
      allSections.forEach(sec => {
        if (permissions[sec]) {
          Object.keys(permissions[sec]).forEach(key => {
            if (permissions[sec][key]) {
              handlePermissionChange(sec, key, false);
            }
          });
        }
      });
    } else {
      // If the view permission is not checked, don't allow other permissions to be checked
      if (section !== 'general' || permissionKey !== 'view') {
        if (checked && !permissions?.general?.view) {
          // If trying to check a permission but view is not enabled, don't allow it
          return;
        }
      }
      
      // Normal case: just update the specific permission
      handlePermissionChange(section, permissionKey, checked);
    }
  };

  const renderCheckbox = (section, key, label, isHeader = false) => (
    <div className={`checkbox-div ${isHeader ? 'header' : ''}`} key={`${section}-${key}`}>
      <Checkbox
        checked={permissions[section]?.[key] || false}
        onChange={(e) => handleCheckboxChange(section, key, e.target.checked)}
        disabled={disabledCheckBox}
      >
        <span className={isHeader ? 'uppercase-label' : 'normal-label'}>
          {label}
        </span>
      </Checkbox>
    </div>
  );
  
  const renderSection = (section, title, items) => {
    const isFabrication = section === 'fabrication';
    const isQCRelease = section === 'qc_release';
    const isCorrosion = section === 'corrosion_protection';
    const isDelivery = section === 'delivery';
    const isSiteReceipt = section === 'site_receipt';
    const isInstallation = section === 'installation';
    const isDeliveryNotes = section === 'delivery_notes';
    const isGeneral = section === 'general';
    const isEdit = section === 'edit';
    
    return (
      <Col 
        className={`project-permission-section multiple-col ${
          isGeneral ? 'general-section' :
          isEdit ? 'edit-section' :
          isFabrication ? 'fabrication-section' : 
          isQCRelease ? 'qc-release-section' :
          isCorrosion ? 'corrosion-section' :
          isDelivery ? 'delivery-section' :
          isSiteReceipt ? 'site-receipt-section' :
          isInstallation ? 'installation-section' :
          isDeliveryNotes ? 'delivery-notes-section' : ''
        }`} 
        style={{ marginRight: '5px' }}
      >
        <div className="label-div">
          <span className="section-title">
            {title}
          </span>
        </div>
        {items.map(([key, label]) => renderCheckbox(section, key, label))}
      </Col>
    );
  };

  const generalItems = [
    ['view', 'View'],
    ['import', 'Import'],
    ['export', 'Export']
  ];

  const deliveryNotesItems = [
    ['generate', 'Generate'],
    ['edit', 'Edit'],
    ['edit_completed_delivery_notes', 'Edit Completed Delivery Notes']
  ];

  const editItems = [
    ['admin_add_edit_delete', 'Admin Add, Edit & Delete'],
    ['newPriority', 'New Priority'],
    ['comments', 'Comments'],
    ['status', 'Status'],
    ['template_fields', 'Template Fields'],
    ['add_revisions', 'Add Revisions'],
    ['adjust_qty_manually', 'Adjust Qty Manually (App Only)']
  ];

  const fabricationItems = [
    ['mark_received', 'Mark Received'],
    ['mark_completed', 'Mark Completed'],
    ['update_supplier', 'Update Supplier'],
    ['add_attachments', 'Add Attachments']
  ];

  const qcItems = [
    ['mark_received', 'Mark Received'],
    ['mark_completed', 'Mark Completed'],
    ['update_supplier', 'Update Supplier'],
    ['add_attachments', 'Add Attachments']
  ];

  const corrosionItems = [
    ['mark_received', 'Mark Received'],
    ['mark_completed', 'Mark Completed'],
    ['update_supplier', 'Update Supplier'],
    ['add_attachments', 'Add Attachments']
  ];

  const deliveryItems = [
    ['mark_received', 'Mark Received'],
    ['mark_completed', 'Mark Completed'],
    ['update_supplier', 'Update Supplier'],
    ['add_attachments', 'Add Attachments']
  ];

  const siteItems = [
    ['mark_received', 'Mark Received'],
    ['mark_completed', 'Mark Completed'],
    ['update_supplier', 'Update Supplier'],
    ['add_attachments', 'Add Attachments'],
    ['add_edit_grv', 'Add & Edit GRV#']
  ];

  const installationItems = [
    ['mark_installed', 'Mark Installed'],
    ['add_attachments', 'Add Attachments']
  ];

  return (
    <div className="table-permission-section">
      <div className="section-title">
        MATERIAL CONTROL {projectStatus && `- ${projectStatus}`}
      </div>
      <Row className="permission-row-div" style={{ display: 'flex', flexWrap: 'wrap' }}>
        {renderSection('general', 'GENERAL', generalItems)}
        {renderSection('edit', 'EDIT', editItems)}
        {renderSection('fabrication', 'FABRICATION', fabricationItems)}
        {renderSection('qc_release', 'QC RELEASE', qcItems)}
        {renderSection('corrosion_protection', 'CORROSION PROTECTION', corrosionItems)}
        {renderSection('delivery', 'DELIVERY TO SITE', deliveryItems)}
        {renderSection('site_receipt', 'SITE RECEIPT', siteItems)}
        {renderSection('installation', 'INSTALLATION', installationItems)}
      </Row>
      <Row className="permission-row-div" style={{ display: 'flex', flexWrap: 'wrap' }}>
        {renderSection('delivery_notes', 'DELIVERY NOTES', deliveryNotesItems)}
      </Row>
    </div>
  );
};

export default TablePermissionsSection;
